import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Flame,
  Wrench,
  Droplet,
  Gauge,
  Thermometer,
  ShieldCheck,
  Check,
  ArrowRight,
  Stethoscope,
  ClipboardList,
  CookingPot,
  ShowerHead,
  Minus,
  Plus,
  HelpCircle,
} from "lucide-react";
import Seo from "@/components/Seo";
import { COMPANY } from "@/lib/site";

const HEAT_IMG =
  "https://images.pexels.com/photos/34938442/pexels-photo-34938442.jpeg";
const PLUMB_IMG =
  "https://images.unsplash.com/photo-1676210134188-4c05dd172f89?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBwbHVtYmVyJTIwaGVhdGluZyUyMGVuZ2luZWVyfGVufDB8fHx8MTc4MTg2NTUyMXww&ixlib=rb-4.1.0&q=85";

const FAQS = [
  {
    q: "Are you Gas Safe registered?",
    a: "Yes. EL-Gas and Plumbing is Gas Safe registered under number 604161. Every engineer carries their Gas Safe ID card on arrival — you can verify any registered engineer on the official Gas Safe Register at gassaferegister.co.uk. Working on gas appliances without being Gas Safe registered is illegal in the UK, so always check the card before letting anyone touch your boiler, hob or gas pipework.",
  },
  {
    q: "Which areas in Hackney and Islington do you cover?",
    a: "We cover the whole of Hackney and Islington from our Median Rd base in Lower Clapton (E5). Day to day that means Lower Clapton, Upper Clapton, Clapton Pond, Hackney Central, Hackney Wick, Dalston, Stoke Newington, Homerton and De Beauvoir in E5/E8/E9, and Islington, Highbury, Canonbury, Barnsbury, Angel and Finsbury Park in N1/N5/N7. If you're on the edge of these areas just give us a call — we'll confirm straight away.",
  },
  {
    q: "How quickly can you attend an emergency plumbing or boiler call-out?",
    a: "During our opening hours (Mon–Fri 09:00–18:00 and Sat 09:00–18:30) most E5, E8, E9, N1 and N5 call-outs are attended within 60–90 minutes. If you have a burst pipe, a leak coming through a ceiling or no heating in winter, call 07930 657151 directly rather than booking online — Ali will give you a realistic ETA and talk you through isolating the water or gas while we're on the way.",
  },
  {
    q: "How much does a Landlord Gas Safety Certificate (CP12) cost in Hackney or Islington?",
    a: "A single-property CP12 inspection is in the £65–£110 range depending on the number of gas appliances and location, and we issue the digital certificate the same day. For agents and landlords with multiple properties we offer portfolio pricing — contact us with the number of properties and we'll put together a tailored quote. Legally you must have a valid CP12 renewed every 12 months and give tenants a copy within 28 days of the inspection.",
  },
  {
    q: "Do I legally need an annual gas safety check as a landlord?",
    a: "Yes. Under the Gas Safety (Installation and Use) Regulations 1998, any landlord in England renting out a property with gas appliances, pipework or flues must arrange an annual safety check by a Gas Safe registered engineer. Tenants must be given a copy of the CP12 within 28 days of the check (or before they move in for new lets), and you must keep records for at least two years. Non-compliance can result in unlimited fines and, in serious cases, prosecution.",
  },
  {
    q: "What's the difference between a basic boiler service and a full strip-down?",
    a: "A basic annual service is a 12-point inspection: we check the burner, pressure, flue and ventilation, carry out a combustion test, perform the 29.6 safety check and issue a service record. A full strip-down is a deeper overhaul — the burner comes out, seals and gaskets are inspected, and the boiler is re-commissioned. Most domestic boilers only need the basic service every year; a strip-down is typically recommended every 3–5 years, or sooner if the boiler is older or has been noisy or unreliable.",
  },
  {
    q: "Which boiler brands do you install?",
    a: "We install Worcester Bosch, Vaillant, Ideal and Glow-worm as standard — these are the brands we trust for London properties and the ones we can register warranties for (typically 7–10 years). If you've researched a specific model, let us know and we'll quote on it. Pricing depends on the model, your property's existing pipework and whether you're converting system type (e.g. regular to combi), so we'll do a free home survey and give you a written quote before any work starts.",
  },
  {
    q: "Can you install a gas hob or freestanding cooker?",
    a: "Yes. We install gas hobs and freestanding cookers including the gas tightness test and compliance paperwork, and we can take the old appliance away. Pricing depends on your existing setup — in particular whether you have a bayonet fitting or need new rigid pipework, and whether an isolation valve is already in place. We'll confirm the exact price on site before starting; it's always a fixed quote, never time-and-materials.",
  },
  {
    q: "My radiators are cold at the top or make banging noises — what should I do?",
    a: "Cold tops usually mean air in the system — a quick bleed with a radiator key often fixes it. If the radiators are cold at the bottom or banging/kettling when the boiler fires, that's typically sludge build-up and the system needs a power-flush with a magnetic filter fitted afterwards. We do both as part of our heating service. If you're also losing pressure on the boiler gauge, call us — there's likely a leak somewhere in the system that needs diagnosing.",
  },
  {
    q: "How do I book, and when will I hear back?",
    a: "You can book online in about 60 seconds from the Booking page — pick the service, choose a morning or afternoon window on your preferred date, and leave your contact details. We'll call you back during opening hours (usually within the hour) to confirm the slot and talk through the job. For emergencies, call 07930 657151 directly rather than using the form so Ali can prioritise you properly.",
  },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
};

const PRICING = [
  {
    title: "Standard Call-Out",
    range: "£75 – £95",
    note: "Within Hackney, Islington & Lower Clapton, weekdays.",
    icon: Wrench,
    features: ["Up to 45-min diagnostic", "Fixed quote before work", "No-fix, half-fee policy"],
  },
  {
    title: "Basic Boiler Service",
    range: "£90 flat",
    note: "Full annual service incl. 29.6 check, certificate issued same day.",
    icon: Flame,
    highlight: true,
    features: ["12-point inspection", "Combustion test", "29.6 safety check", "Service record issued"],
  },
  {
    title: "Full Strip-Down Service",
    range: "£180 flat",
    note: "Deep clean & overhaul — recommended every 3–5 years.",
    icon: Wrench,
    features: ["Burner removed & cleaned", "Heat exchanger flushed", "Seals & gaskets checked"],
  },
  {
    title: "Gas Safety Check (CP12)",
    range: "£65 – £110",
    note: "Per property. Discounts for portfolios.",
    icon: ShieldCheck,
    features: ["Official CP12 cert", "Tenant copy supplied", "Same-day digital PDF"],
  },
];

export default function ServicesPage() {
  const [openIndex, setOpenIndex] = useState(0);
  return (
    <>
      <Seo
        title="Services & Pricing | EL-Gas and Plumbing — Hackney & Islington"
        description="Boiler servicing, repairs, installations, CP12 landlord certificates, gas hobs, taps & showers. Transparent pricing from Gas Safe engineers in Hackney & Islington."
        path="/services"
        jsonLd={FAQ_JSONLD}
      />
      {/* Header */}
      <section className="bg-brand-navy text-white py-20" data-testid="services-hero">
        <div className="container-x">
          <span className="overline text-brand-orange">Services</span>
          <h1 className="mt-3 font-heading font-black text-5xl md:text-6xl tracking-tighter">
            Trade-grade work. <span className="text-brand-orange">Honest pricing.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-slate-300 text-lg">
            From a leaking radiator to a full system upgrade — every job done to manufacturer spec
            and to Gas Safe Register standards.
          </p>
        </div>
      </section>

      {/* Section 1: Boiler & Heating */}
      <section className="py-24 bg-white" data-testid="services-boiler-section">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="absolute -top-4 -left-4 right-4 bottom-4 border-2 border-brand-orange rounded-sm" />
            <img
              src={HEAT_IMG}
              alt="Heating engineer servicing boiler"
              className="relative w-full h-[460px] object-cover rounded-sm shadow-xl"
            />
          </div>
          <div className="order-1 lg:order-2">
            <span className="overline">01 — Heating</span>
            <h2 className="mt-3 font-heading font-black text-4xl md:text-5xl tracking-tighter text-brand-navy">
              Boiler &amp; Heating Solutions
            </h2>
            <p className="mt-5 text-slate-700 leading-relaxed">
              Whether you’re replacing a 20-year-old combi or installing a high-efficiency Worcester
              Bosch system, our engineers handle the design, install and commissioning. We power-flush,
              fit smart thermostats and register your warranty for you — typically 7–10 years.
            </p>
            <ul className="mt-7 grid sm:grid-cols-2 gap-3">
              {[
                "Combi, system & regular installs",
                "System conversion (regular ↔ combi)",
                "Annual servicing & safety check",
                "Power-flushing & magnetic filters",
                "Smart thermostat setup (Nest / Hive)",
                "Underfloor heating commissioning",
                "Warranty registration",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm font-semibold text-slate-800">
                  <Check className="w-4 h-4 text-brand-orange mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <Link to="/booking" className="btn-primary mt-9" data-testid="services-boiler-cta">
              Book a heating engineer <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Section 2: Emergency Plumbing */}
      <section className="py-24 bg-slate-50" data-testid="services-plumbing-section">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="overline">02 — Emergency Plumbing</span>
            <h2 className="mt-3 font-heading font-black text-4xl md:text-5xl tracking-tighter text-brand-navy">
              Leaks, bursts, blockages — <span className="text-brand-orange">on it.</span>
            </h2>
            <p className="mt-5 text-slate-700 leading-relaxed">
              When the ceiling’s dripping at midnight, you don’t want a call-centre — you want a local
              van. We respond fast across E5, E8, E9, N1 and N5 with stop-cock kits, drainage rods
              and replacement parts on board.
            </p>
            <ul className="mt-7 grid sm:grid-cols-2 gap-3">
              {[
                "Rapid leak detection",
                "Burst pipe repairs",
                "Radiator replacements",
                "Drainage blockages",
                "Tap, valve & cylinder repairs",
                "Insurance-ready reports",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm font-semibold text-slate-800">
                  <Check className="w-4 h-4 text-brand-orange mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <a href={`tel:${COMPANY.phoneTel}`} className="btn-primary" data-testid="services-plumbing-call">
                Emergency line: {COMPANY.phone}
              </a>
              <Link to="/booking" className="btn-outline-navy" data-testid="services-plumbing-book">
                Book a slot
              </Link>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -top-4 -right-4 left-4 bottom-4 border-2 border-brand-orange rounded-sm" />
            <img
              src={PLUMB_IMG}
              alt="Plumber working on pipe"
              className="relative w-full h-[460px] object-cover rounded-sm shadow-xl"
            />
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-24 bg-white" data-testid="services-additional-section">
        <div className="container-x">
          <div className="max-w-3xl">
            <span className="overline">More we cover</span>
            <h2 className="mt-3 font-heading font-black text-4xl md:text-5xl tracking-tighter text-brand-navy">
              From a dripping tap to a brand-new cooker line.
            </h2>
            <p className="mt-5 text-slate-700 max-w-2xl">
              Beyond the headline jobs, our Gas Safe engineers handle the full range of domestic
              gas and plumbing fittings across E5 and the wider Hackney &amp; Islington area.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Stethoscope,
                title: "Boiler Repair & Diagnostic",
                body: "Full-suite diagnostic on site. Once we identify the fault we'll talk you through the fix and confirm the price before any work starts.",
                points: ["Error code analysis", "Pressure & flow checks", "Replacement parts on van", "Price confirmed after diagnosis"],
              },
              {
                icon: ClipboardList,
                title: "Boiler Quote & Installation",
                body: "Pricing depends on the boiler model you choose and the layout of your property — we'll talk you through the options and quote in writing.",
                points: ["Free home survey", "Worcester / Vaillant / Ideal / Glow-worm", "29.6 safety check on install", "Warranty registered for you", "Quote tailored to your spec"],
              },
              {
                icon: CookingPot,
                title: "Gas Hob & Cooker Installation",
                body: "Cost depends on your existing setup, connection type and any gas-line work needed — we'll confirm the price once we've seen it on the day.",
                points: ["Gas tightness test", "Compliance certificate", "Old appliance removed", "Price confirmed on site"],
              },
              {
                icon: ShowerHead,
                title: "Taps & Shower Fittings",
                body: "Pricing varies by tap or shower model and pressure setup — happy to liaise with you on the right product and total price.",
                points: ["Mixer, monobloc & electric showers", "Pressure balanced install", "Supply or fit-only", "Price discussed beforehand"],
              },
            ].map((s) => (
              <article
                key={s.title}
                data-testid={`additional-service-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="bg-slate-50 border border-slate-200 p-7 rounded-sm shadow-sm hover:shadow-md hover:border-brand-orange/60 transition-all"
              >
                <div className="grid place-items-center w-12 h-12 bg-brand-navy text-white rounded-sm">
                  <s.icon className="w-6 h-6 text-brand-orange" />
                </div>
                <h3 className="mt-5 font-heading font-bold text-xl tracking-tight text-brand-navy">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.body}</p>
                <ul className="mt-4 space-y-2">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="w-4 h-4 text-brand-orange mt-0.5 shrink-0" />
                      <span className="font-medium">{p}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Transparency */}
      <section className="py-24 bg-white" data-testid="pricing-section">
        <div className="container-x">
          <div className="max-w-3xl">
            <span className="overline">Pricing Transparency</span>
            <h2 className="mt-3 font-heading font-black text-4xl md:text-5xl tracking-tighter text-brand-navy">
              Know the price <span className="text-brand-orange">before the spanner comes out.</span>
            </h2>
            <p className="mt-5 text-slate-700 max-w-2xl">
              Every job is quoted up-front. The numbers below are standard ranges so you know what to
              expect before you call.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PRICING.map((p) => (
              <article
                key={p.title}
                data-testid={`pricing-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`relative bg-white border ${
                  p.highlight ? "border-brand-orange shadow-xl" : "border-slate-200 shadow-sm"
                } p-8 rounded-sm flex flex-col`}
              >
                {p.highlight && (
                  <span className="absolute -top-3 left-6 bg-brand-orange text-white text-[10px] font-bold tracking-widest px-3 py-1 rounded-sm">
                    MOST BOOKED
                  </span>
                )}
                <div className="grid place-items-center w-12 h-12 bg-brand-navy text-white rounded-sm">
                  <p.icon className="w-6 h-6 text-brand-orange" />
                </div>
                <h3 className="mt-6 font-heading font-black text-2xl tracking-tight text-brand-navy">
                  {p.title}
                </h3>
                <p className="mt-3 font-heading font-black text-4xl tracking-tighter text-slate-900">
                  {p.range}
                </p>
                <p className="mt-2 text-sm text-slate-600">{p.note}</p>
                <ul className="mt-6 space-y-2 text-sm">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-slate-700">
                      <Check className="w-4 h-4 text-brand-orange mt-0.5 shrink-0" />
                      <span className="font-medium">{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/booking"
                  className={`mt-7 ${p.highlight ? "btn-primary" : "btn-outline-navy"} w-full`}
                  data-testid={`pricing-cta-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  Book this service
                </Link>
              </article>
            ))}
          </div>

          <div className="mt-10 p-6 bg-slate-50 border-l-4 border-brand-orange rounded-sm flex flex-col md:flex-row md:items-center gap-4 justify-between">
            <p className="text-slate-800">
              <span className="font-bold">Note:</span> Out-of-hours and weekend emergency callouts have
              a small surcharge — always quoted up front before dispatch.
            </p>
            <Link to="/contact" className="btn-outline-navy shrink-0" data-testid="pricing-quote-cta">
              Get a written quote
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-slate-50" data-testid="services-faq-section">
        <div className="container-x">
          <div className="max-w-3xl">
            <span className="overline inline-flex items-center gap-2">
              <HelpCircle className="w-4 h-4" /> Frequently asked
            </span>
            <h2 className="mt-3 font-heading font-black text-4xl md:text-5xl tracking-tighter text-brand-navy">
              Common questions from our Hackney &amp; Islington customers.
            </h2>
            <p className="mt-5 text-slate-700 max-w-2xl">
              Straight answers to the things people ask us the most — about Gas
              Safe compliance, landlord certificates, boiler brands, pricing
              and getting an engineer out quickly in E5, E8, E9, N1 and N5.
            </p>
          </div>

          <div className="mt-12 max-w-4xl space-y-3">
            {FAQS.map((item, i) => {
              const isOpen = i === openIndex;
              return (
                <div
                  key={item.q}
                  className={`bg-white border rounded-sm transition-shadow ${
                    isOpen ? "border-brand-orange shadow-md" : "border-slate-200"
                  }`}
                  data-testid={`faq-item-${i}`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenIndex(isOpen ? -1 : i)}
                    className="w-full flex items-center justify-between gap-4 p-5 md:p-6 text-left"
                    aria-expanded={isOpen}
                    data-testid={`faq-toggle-${i}`}
                  >
                    <span className="font-heading font-bold text-lg md:text-xl tracking-tight text-brand-navy">
                      {item.q}
                    </span>
                    <span
                      className={`grid place-items-center w-9 h-9 rounded-sm shrink-0 ${
                        isOpen ? "bg-brand-orange text-white" : "bg-slate-100 text-brand-navy"
                      }`}
                    >
                      {isOpen ? <Minus className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                    </span>
                  </button>
                  {isOpen && (
                    <div
                      className="px-5 md:px-6 pb-6 text-slate-700 leading-relaxed text-[15px]"
                      data-testid={`faq-answer-${i}`}
                    >
                      {item.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-12 p-6 bg-brand-navy text-white rounded-sm flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div>
              <p className="overline text-brand-orange">Still have a question?</p>
              <p className="mt-2 font-heading font-bold text-xl">
                Call Ali on 07930 657151 or drop us a message — we usually reply within the hour.
              </p>
            </div>
            <Link to="/contact" className="btn-primary shrink-0" data-testid="faq-contact-cta">
              Get in touch <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="py-20 bg-brand-navy text-white">
        <div className="container-x grid md:grid-cols-3 gap-6">
          {[
            { icon: Gauge, title: "Same-day response", body: "Most E5 callouts attended within 90 minutes." },
            { icon: Thermometer, title: "Manufacturer-trained", body: "Worcester, Vaillant, Ideal, Baxi & Glow-worm." },
            { icon: Droplet, title: "Tidy as we go", body: "Dust sheets, shoe covers and a hoover always on board." },
          ].map((i) => (
            <div key={i.title} className="border border-white/10 p-7 rounded-sm bg-white/[0.04]">
              <i.icon className="w-7 h-7 text-brand-orange" />
              <h4 className="mt-4 font-heading font-bold text-xl">{i.title}</h4>
              <p className="mt-2 text-slate-300 text-sm leading-relaxed">{i.body}</p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
